import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from "../styles/ShareButton.module.css";

interface GameResult {
  type: 'success' | 'fail';
  title: string;
  bet: string;
  payout: number;
  payoutToken?: string;
  rowsCompleted: number;
  totalRows: number;
  multiplier: number;
}

interface ShareButtonProps {
  gameResult: GameResult;
}

const ShareButton: React.FC<ShareButtonProps> = ({ gameResult }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US', { maximumFractionDigits: 4 });
  };

  const generateShareContent = (platform: 'twitter' | 'telegram' | 'copy') => {
    const { type, payout, payoutToken, rowsCompleted, totalRows, multiplier } = gameResult;
    const baseUrl = 'https://deathx.fun';
    
    let message = '';
    let url = baseUrl;

    if (type === 'success' && rowsCompleted >= totalRows) {
      // Complete game win
      if (platform === 'twitter') {
        message = `🏆 Just completed ALL ${totalRows} rows in DeathX.Fun and won ${formatNumber(payout)} ${payoutToken || 'BNB'}! 💰 Think you can beat my ${formatNumber(multiplier)}x multiplier? @deathxfun \n\n#DeathXFun #CryptoGaming #BNB`;
      } else {
        message = `🏆 Just completed ALL ${totalRows} rows in DeathX.Fun and won ${formatNumber(payout)} ${payoutToken || 'BNB'}! 💰 Think you can beat my ${formatNumber(multiplier)}x multiplier? Play at ${baseUrl}  \n\n#DeathXFun #CryptoGaming #BNB`;
      }
    } else if (type === 'success') {
      // Cash out
      if (platform === 'twitter') {
        message = `💰 Cashed out at row ${rowsCompleted}/${totalRows} in DeathX.Fun with ${formatNumber(payout)} ${payoutToken || 'BNB'}! 🎯 Smart exit or missed opportunity? @deathxfun  \n\n#DeathXFun #CryptoGaming #BNB`;
      } else {
        message = `💰 Cashed out at row ${rowsCompleted}/${totalRows} in DeathX.Fun with ${formatNumber(payout)} ${payoutToken || 'BNB'}! 🎯 Smart exit or missed opportunity? Play at ${baseUrl}  \n\n#DeathXFun #CryptoGaming #BNB`;
      }
    } else {
      // Loss (with DXF rewards)
      if (platform === 'twitter') {
        message = `💀 Lost at row ${rowsCompleted}/${totalRows} in DeathX.Fun but earned ${formatNumber(payout)} ${payoutToken || 'DXF'} tokens! 🎮 The tension is real... Try your luck @deathxfun \n\n#DeathXFun #CryptoGaming #DXF`;
      } else {
        message = `💀 Lost at row ${rowsCompleted}/${totalRows} in DeathX.Fun but earned ${formatNumber(payout)} ${payoutToken || 'DXF'} tokens! 🎮 The tension is real... Try your luck at ${baseUrl}  \n\n#DeathXFun #CryptoGaming #DXF`;
      }
    }

    return { message, url };
  };

  const handleShare = (platform: 'twitter' | 'telegram' | 'copy') => {
    const { message, url } = generateShareContent(platform);

    switch (platform) {
      case 'twitter':
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`;
        window.open(twitterUrl, '_blank');
        setIsModalOpen(false);
        break;

      case 'telegram':
        const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message.replace("https://deathx.fun", "DeathX.Fun"))}`;
        window.open(telegramUrl, '_blank');
        setIsModalOpen(false);
        break;

      case 'copy':
        copyToClipboard(message);
        break;
    }

  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const renderModal = () => {
    if (!isModalOpen) return null;

    return createPortal(
      <div className={styles.shareModalOverlay} onClick={() => setIsModalOpen(false)}>
        <div className={styles.shareModal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.shareModalHeader}>
            <h3>Share Your Game</h3>
            <button 
              className={styles.shareModalClose}
              onClick={() => setIsModalOpen(false)}
            >
              ✕
            </button>
          </div>

          <div className={styles.shareModalContent}>
            <div className={styles.shareOptions}>
              <div className={styles.shareOption} onClick={() => handleShare('twitter')}>
                <img src="/icons/x-twitter.svg" alt="X" className={styles.shareIcon} />
                <span>Twitter</span>
              </div>
              
              <div className={styles.shareOption} onClick={() => handleShare('telegram')}>
                <img src="/icons/telegram.svg" alt="Telegram" className={styles.shareIcon} />
                <span>Telegram</span>
              </div>
              
              <div className={styles.shareOption} onClick={() => handleShare('copy')}>
                <img src="/icons/copy.svg" alt="Copy" className={styles.shareIcon} />
                <span>Copy Message</span>
              </div>
            </div>

            {copySuccess && (
              <div className={styles.copySuccessModal}>
                ✅ Copied to clipboard!
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <button 
        // className={styles.shareButton}
        className="btn btn-primary"
        onClick={() => setIsModalOpen(true)}
      >
        📤 Share Game
      </button>

      {renderModal()}
    </>
  );
};

export default ShareButton;